import { NetworkConfig } from '../config/networks';

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function shortAddress(address: string): string {
  const trimmed = address.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('0x') && trimmed.length >= 10) {
    return trimmed.slice(-6).toLowerCase();
  }
  // Stellar C… / G… — last 6 chars are distinctive enough
  return trimmed.slice(-6).toLowerCase();
}

function emailHint(email: string): string {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed || !trimmed.includes('@')) return '';
  const [local, domain] = trimmed.split('@');
  const domainLabel = domain?.split('.')[0] || '';
  // Prefer org domain over generic local parts
  if (domainLabel && !['gmail', 'googlemail', 'hotmail', 'outlook', 'yahoo', 'icloud', 'protonmail'].includes(domainLabel)) {
    return slugify(domainLabel);
  }
  return slugify(local || domainLabel);
}

/**
 * Suggest a dApp name from available onboarding context.
 * Example: "acme-stellar-testnet-b27smkd"
 */
export function suggestDAppName(params: {
  email?: string;
  chain?: NetworkConfig | null;
  contractAddress?: string;
}): string {
  const parts: string[] = [];
  const org = params.email ? emailHint(params.email) : '';
  if (org) parts.push(org);
  if (params.chain?.name) parts.push(slugify(params.chain.name));
  const addr = params.contractAddress ? shortAddress(params.contractAddress) : '';
  if (addr) parts.push(addr);

  if (parts.length === 0) return '';
  return parts.join('-');
}

/**
 * Suggest a policy name unique within existing names.
 * Example: "email-otp-acme-stellar-testnet-b27smkd"
 */
export function suggestPolicyName(params: {
  policyType: string;
  dAppName?: string;
  email?: string;
  chain?: NetworkConfig | null;
  contractAddress?: string;
  existingNames?: string[];
}): string {
  if (!params.policyType) return '';

  const typeSlug = slugify(params.policyType);

  const dApp =
    params.dAppName?.trim() ||
    suggestDAppName({
      email: params.email,
      chain: params.chain,
      contractAddress: params.contractAddress,
    });
  const dAppSlug = dApp ? slugify(dApp) : '';

  const base = dAppSlug ? `${typeSlug}-${dAppSlug}` : typeSlug;
  const existing = new Set((params.existingNames || []).map(n => n.trim().toLowerCase()));

  if (!existing.has(base)) {
    return base;
  }

  let suffix = 2;
  while (existing.has(`${base}-${suffix}`)) {
    suffix += 1;
  }
  return `${base}-${suffix}`;
}
